import { useState } from 'react';
import Button from '../../../components/Button';
import { useAuth } from '../../../hooks/useAuth';
import { questsService } from '../services/quests-service';
import type { QuestJourney } from '../services/questTypes';

interface QuestJourneyManagerProps {
    questJourney: QuestJourney | null;
    onJourneyUpdate: () => void;
    journeyDayTotalCount: number;
}

export const QuestJourneyManager = ({ questJourney, onJourneyUpdate, journeyDayTotalCount }: QuestJourneyManagerProps) => {
    const [targetDay, setTargetDay] = useState<string>(questJourney?.currentDay.toString() || '');
    const [isLoading, setIsLoading] = useState(false);
    const { user } = useAuth();

    if (!questJourney) {
        return (
            <div className="bg-gray-100 p-4 rounded-lg mb-6">
                <h2 className="text-xl font-bold mb-2">Quest Journey Manager</h2>
                <p className="text-gray-600">No current quest journey.</p>
            </div>
        );
    }

    const handleJumpToCompleted = async () => {
        if (!user || !targetDay) return;

        const dayNumber = parseInt(targetDay);
        if (isNaN(dayNumber) || dayNumber < 0) {
            alert('Please enter a valid day number (0 or greater)');
            return;
        }

        const confirmed = window.confirm(
            dayNumber === 0
                ? 'Are you sure you want to delete the quest journey? This will remove all progress.'
                : `Are you sure you want to jump to day ${dayNumber}? This will set the journey to day ${dayNumber - 1} and mark it as completed.`
        );

        if (!confirmed) return;

        setIsLoading(true);
        try {
            if (dayNumber === 0) {
                await questsService.deleteUserJourney(user.uid);
            } else {
                await questsService.updateUserJourney(user.uid, dayNumber - 1, true);
            }
            onJourneyUpdate();
        } catch (error) {
            console.error('Error updating journey:', error);
            alert('Failed to update journey. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleJumpToIncomplete = async () => {
        if (!user || !targetDay) return;

        const dayNumber = parseInt(targetDay);
        if (isNaN(dayNumber)) {
            alert('Please select a valid day number');
            return;
        }

        const confirmed = window.confirm(
            `Are you sure you want to jump to day ${dayNumber}? This will set the journey to day ${dayNumber} and mark it as incomplete.`
        );

        if (!confirmed) return;

        setIsLoading(true);
        try {
            await questsService.jumpToDay(user.uid, dayNumber);
            onJourneyUpdate();
        } catch (error) {
            console.error('Error updating journey:', error);
            alert('Failed to update journey. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="mb-6">
            <h2 className="text-xl font-bold mb-4 text-blue-800">Current Quest Journey (Day {questJourney.currentDay} {questJourney.isCurrentDayCompleted ? 'Completed' : 'Not Completed'})</h2>

            <div className="flex flex-wrap gap-2 items-center">
                <Button
                    onClick={handleJumpToCompleted}
                    variant="primary"
                    disabled={!targetDay || isLoading}
                >
                    {isLoading ? 'Processing...' : 'Jump (Previous Day Complete)'}
                </Button>
                <select
                    id="targetDay"
                    value={targetDay}
                    onChange={(e) => setTargetDay(e.target.value)}
                    className="px-3 py-2 border-r-8 border-transparent outline outline-neutral-700 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 w-24"
                    disabled={isLoading}
                >
                    {Array.from({ length: journeyDayTotalCount }, (_, index) => (
                        <option key={index} value={index.toString()}>{index}</option>
                    ))}
                </select>
                <Button
                    onClick={handleJumpToIncomplete}
                    variant="success"
                    disabled={!targetDay || isLoading}
                >
                    {isLoading ? 'Processing...' : 'Jump (Mark as Incomplete)'}
                </Button>
            </div>

            <div className="mt-3 text-sm text-gray-600">
                <p><strong>Jump Options:</strong></p>
                <ul className="list-disc list-inside space-y-1 mt-1">
                    <li><strong>Jump (Previous Day Complete):</strong> For when you want to regenerate the quests for the selected day.</li>
                    <li><strong>Jump (Incomplete):</strong> For when you want to move to this day and test the existing quests.</li>
                </ul>
            </div>
        </div>
    );
};